import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LuluVerify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claimIdFromUrl = searchParams.get('claim_id');

  const [claims, setClaims] = useState([]);
  const [selectedClaimId, setSelectedClaimId] = useState(claimIdFromUrl || '');
  const [photos, setPhotos] = useState([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle, loading, success, error

  useEffect(() => {
    fetchClaims();
    if (claimIdFromUrl) {
      setSelectedClaimId(claimIdFromUrl);
    }
  }, [claimIdFromUrl]);

  const fetchClaims = async () => {
    try {
      const response = await axios.get(`${API}/claims`);
      setClaims(response.data);
    } catch (error) {
      console.error('Error fetching claims:', error);
    }
  };

  const captureLocation = () => {
    setGpsStatus('loading');
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          setGpsStatus('success');
        },
        (error) => {
          console.error('GPS error:', error);
          setGpsStatus('error');
          alert('Could not get location. Please enable GPS.');
        }
      );
    } else {
      setGpsStatus('error');
      alert('Geolocation not supported by your browser.');
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prevPhotos => [...prevPhotos, ...files]);

    // Create preview URLs
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviewUrls(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClaimId) {
      alert('Please select a claim');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      photos.forEach(photo => {
        formData.append('photos', photo);
      });

      // Create evidence data as JSON
      const evidenceData = {
        latitude,
        longitude,
        verification_notes: verificationNotes
      };

      // Send as multipart with JSON in form field
      formData.append('evidence_data', JSON.stringify(evidenceData));

      // For now, send as separate body param (FastAPI handles this differently)
      const response = await axios.post(
        `${API}/claims/${selectedClaimId}/evidence`,
        evidenceData,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      setSuccess(true);
      setTimeout(() => {
        navigate(`/intelligence?claim_id=${selectedClaimId}`);
      }, 2000);
    } catch (error) {
      console.error('Error uploading evidence:', error);
      alert('Failed to upload evidence. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-slate-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/')} className="text-white/60 hover:text-white transition-colors">
              ← Back
            </button>
            <div className="flex items-center space-x-3">
              <img src="/veritascore-logo.png" alt="VeritasCore" className="h-10 w-10" />
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">VERITASCORE.IO</h1>
                <p className="text-xs text-green-400">LuluVerify — Capture the Proof</p>
              </div>
            </div>
          </div>
          <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {!success ? (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-white mb-6">Add Evidence</h2>
            <p className="text-gray-300 mb-2">Upload photos, GPS location, and verification details.</p>
            <p className="text-xs text-teal-400 mb-8">Verification complete — ready for VeritasCore</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Select Claim */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Select Claim</label>
                <select
                  value={selectedClaimId}
                  onChange={(e) => setSelectedClaimId(e.target.value)}
                  required
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  data-testid="claim-select"
                >
                  <option value="">Choose a claim...</option>
                  {claims.map(claim => (
                    <option key={claim.id} value={claim.id}>
                      {claim.title} - {claim.incident_type}
                    </option>
                  ))}
                </select>
              </div>

              {/* GPS Location */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
                {gpsStatus === 'idle' && (
                  <button
                    type="button"
                    onClick={captureLocation}
                    className="w-full bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 rounded-lg px-6 py-4 text-green-300 font-semibold flex items-center justify-center space-x-3 transition-all"
                    data-testid="capture-location-btn"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Capture GPS Location</span>
                  </button>
                )}
                {gpsStatus === 'loading' && (
                  <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg px-6 py-4 text-blue-300">
                    Getting your location...
                  </div>
                )}
                {gpsStatus === 'success' && latitude && longitude && (
                  <div className="bg-green-500/20 border border-green-500/50 rounded-lg px-6 py-4 text-green-300">
                    <div className="flex items-center justify-between">
                      <span>✓ Location captured: {latitude.toFixed(6)}, {longitude.toFixed(6)}</span>
                      <button
                        type="button"
                        onClick={() => { setLatitude(null); setLongitude(null); setGpsStatus('idle'); }}
                        className="text-red-300 hover:text-red-200"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Photos</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                  data-testid="photo-upload-input"
                />
                <label
                  htmlFor="photo-upload"
                  className="w-full bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 rounded-lg px-6 py-4 text-green-300 font-semibold flex items-center justify-center space-x-3 transition-all cursor-pointer"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Upload Photos</span>
                </label>

                {/* Photo Previews */}
                {photoPreviewUrls.length > 0 && (
                  <div className="mt-4 photo-grid">
                    {photoPreviewUrls.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-40 object-cover rounded-lg border border-white/20"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Verification Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Verification Notes (Optional)</label>
                <textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Add any additional verification details..."
                  rows={4}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                  data-testid="verification-notes-input"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 rounded-lg transition-all"
                data-testid="submit-evidence-btn"
              >
                {loading ? 'Uploading Evidence...' : 'Submit Evidence'}
              </button>
            </form>
          </div>
        ) : (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Evidence Verified!</h2>
            <p className="text-gray-300 mb-2">Your evidence has been captured and timestamped.</p>
            <p className="text-xs text-teal-400 mb-8">Analyzed by VeritasCore — generating report...</p>
          </div>
        )}
      </div>
    </div>
  );
}